import io.ktor.client.call.*
import io.ktor.client.request.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.*

@Serializable
data class TourJokerUseDto(@SerialName("player_id") val playerId: String, @SerialName("entry_id") val entryId: String)

@Serializable
data class TourHandicapSetDto(
    val id: String,
    @SerialName("season_id") val seasonId: String,
    @SerialName("source_season_name") val sourceSeasonName: String,
    @SerialName("effective_from") val effectiveFrom: String,
    val status: String, val revision: Long,
    @SerialName("created_at") val createdAt: String = "",
    @SerialName("source_note") val sourceNote: String = "",
    @SerialName("first_tournament_id") val firstTournamentId: String? = null,
)

@Serializable
data class TourHandicapDto(
    val id: String,
    @SerialName("set_id") val setId: String,
    @SerialName("player_id") val playerId: String,
    @SerialName("player_name") val playerName: String,
    val gender: String? = null, val games: Int = 0,
    @SerialName("source_average") val sourceAverage: Double? = null,
    @SerialName("adjusted_average") val adjustedAverage: Double? = null,
    @SerialName("calculated_group") val calculatedGroup: String? = null,
    @SerialName("previous_group") val previousGroup: String? = null,
    @SerialName("group_code") val groupCode: String? = null,
    @SerialName("is_manual") val isManual: Boolean = false,
    val reason: String = "",
)

@Serializable
data class TourSlotDto(
    val id: String,
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("round_no") val roundNo: Int,
    @SerialName("starts_at") val startsAt: String,
    val capacity: Int,
    @SerialName("roster_version") val rosterVersion: Long,
    @SerialName("started_at") val startedAt: String? = null,
    @SerialName("current_draw_id") val currentDrawId: String? = null,
)

@Serializable
data class TourEntryDto(
    val id: String,
    @SerialName("tournament_id") val tournamentId: String,
    @SerialName("player_id") val playerId: String,
    @SerialName("player_name") val playerName: String,
    @SerialName("slot_id") val slotId: String? = null,
    @SerialName("waitlist_order") val waitlistOrder: Int? = null,
    @SerialName("waitlist_slot_id") val waitlistSlotId: String? = null,
    val attendance: String, val joker: Boolean,
    @SerialName("handicap_group") val handicapGroup: String? = null,
    val handicap: Int? = null,
    @SerialName("handicap_frozen_at") val handicapFrozenAt: String? = null,
)

@Serializable
data class TourDrawDto(
    val id: String,
    @SerialName("slot_id") val slotId: String,
    @SerialName("roster_version") val rosterVersion: Long,
    val generation: Int,
)

@Serializable
data class TourDrawEntryDto(
    val id: String,
    @SerialName("draw_id") val drawId: String,
    @SerialName("entry_id") val entryId: String,
    val lane: Int, val position: Int,
    @SerialName("moved_from_lane") val movedFromLane: Int? = null,
    @SerialName("moved_from_position") val movedFromPosition: Int? = null,
)

object TourOperationsApi {
    suspend fun handicapRoster(token: String, setId: String): Set<String> = httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_handicap_roster") {
        tourAuth(token); setBody(buildJsonObject { put("p_set_id", setId) })
    }.body<List<String>>().toSet()
    suspend fun beginPresentation(token: String, t: TourTournamentDto) = call(token, "tour_begin_presentation", buildJsonObject {
        put("p_tournament_id", t.id); put("p_expected_revision", t.registrationRevision)
    })
    suspend fun jokerUses(token: String, seasonId: String): List<TourJokerUseDto> = httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_season_joker_uses") {
        tourAuth(token); setBody(buildJsonObject { put("p_season_id", seasonId) })
    }.body()

    suspend fun startedPlayers(token: String, seasonId: String): Set<String> = httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_season_started_players") {
        tourAuth(token); setBody(buildJsonObject { put("p_season_id", seasonId) })
    }.body<List<String>>().toSet()

    private suspend fun call(token: String, name: String, body: JsonObject) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/$name") { tourAuth(token); setBody(body) }
    }
    suspend fun sets(token: String?, seasonId: String): List<TourHandicapSetDto> =
        TourApi.rows("tour_handicap_set", token, "effective_from.desc,created_at.desc,id", mapOf("season_id" to "eq.$seasonId"))
    suspend fun handicaps(token: String, setId: String): List<TourHandicapDto> =
        TourApi.rows("tour_handicap_player", token, "player_name,id", mapOf("set_id" to "eq.$setId"))
    suspend fun publicHandicaps(setId: String): List<TourHandicapDto> {
        val rows = mutableListOf<TourHandicapDto>()
        while (true) {
            val page: List<TourHandicapDto> = httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_public_handicaps") {
                tourAuth(null); setBody(buildJsonObject { put("p_set_id", setId); put("p_offset", rows.size) })
            }.body()
            rows.addAll(page)
            if (page.size < 200) return rows
        }
    }
    suspend fun prepare(token: String, id: String, seasonId: String, sourceId: String, tournamentId: String) =
        call(token, "tour_prepare_handicaps", buildJsonObject {
            put("p_id", id); put("p_season_id", seasonId); put("p_source_season_id", sourceId); put("p_first_tournament_id", tournamentId)
        })
    suspend fun assign(token: String, set: TourHandicapSetDto, playerId: String, group: String?, reason: String) =
        call(token, "tour_set_handicap", buildJsonObject {
            put("p_set_id", set.id); put("p_player_id", playerId); put("p_group", group?.let(::JsonPrimitive) ?: JsonNull)
            put("p_reason", reason); put("p_expected_revision", set.revision)
        })
    suspend fun confirm(token: String, set: TourHandicapSetDto) = call(token, "tour_confirm_handicaps", buildJsonObject {
        put("p_set_id", set.id); put("p_expected_revision", set.revision)
    })
    suspend fun tournament(token: String?, id: String): TourTournamentDto =
        TourApi.rows<TourTournamentDto>("tour_tournament", token, "id", mapOf("id" to "eq.$id")).single()
    suspend fun tournamentBySlug(slug: String): TourTournamentDto? =
        TourApi.rows<TourTournamentDto>("tour_tournament", null, "id", mapOf("slug" to "eq.$slug")).singleOrNull()
    suspend fun slots(token: String?, id: String): List<TourSlotDto> =
        TourApi.rows("tour_slot", token, "round_no,id", mapOf("tournament_id" to "eq.$id"))
    suspend fun entries(token: String?, id: String): List<TourEntryDto> =
        TourApi.rows("tour_entry", token, "created_at,id", mapOf("tournament_id" to "eq.$id"))
    suspend fun draws(token: String?, slotId: String): List<TourDrawDto> =
        TourApi.rows("tour_draw", token, "generation.desc,id", mapOf("slot_id" to "eq.$slotId"))
    suspend fun drawEntries(token: String?, drawId: String): List<TourDrawEntryDto> =
        TourApi.rows("tour_draw_entry", token, "lane,position,id", mapOf("draw_id" to "eq.$drawId"))
    suspend fun prepareSlots(token: String, t: TourTournamentDto) = call(token, "tour_prepare_slots", buildJsonObject {
        put("p_tournament_id", t.id); put("p_expected_revision", t.registrationRevision)
    })
    suspend fun saveSlot(token: String, t: TourTournamentDto, slot: TourSlotDto, time: String, capacity: Int) =
        call(token, "tour_update_slot", buildJsonObject {
            put("p_slot_id", slot.id); put("p_starts_at", time); put("p_capacity", capacity); put("p_expected_revision", t.registrationRevision)
        })
    suspend fun register(token: String, t: TourTournamentDto, playerId: String, slotId: String?) =
        call(token, "tour_register_entry", buildJsonObject {
            put("p_tournament_id", t.id); put("p_player_id", playerId)
            put("p_slot_id", slotId?.let(::JsonPrimitive) ?: JsonNull); put("p_expected_revision", t.registrationRevision)
        })
    suspend fun entry(token: String, t: TourTournamentDto, e: TourEntryDto, attendance: String, joker: Boolean) =
        call(token, "tour_update_entry", buildJsonObject {
            put("p_entry_id", e.id); put("p_attendance", attendance); put("p_joker", joker); put("p_expected_revision", t.registrationRevision)
        })
    suspend fun draw(token: String, t: TourTournamentDto, slot: TourSlotDto) = call(token, "tour_draw_slot", buildJsonObject {
        put("p_slot_id", slot.id); put("p_expected_revision", t.registrationRevision)
    })
    internal suspend fun manualDraw(token: String, t: TourTournamentDto, slot: TourSlotDto, placements: List<TourManualPlacement>) =
        call(token, "tour_save_manual_draw", buildJsonObject {
            put("p_slot_id", slot.id); put("p_expected_revision", t.registrationRevision)
            put("p_placements", JsonArray(placements.map { row -> buildJsonObject {
                put("entry_id", row.entryId); put("lane", row.lane); put("position", row.position)
            } }))
        })
    suspend fun start(token: String, t: TourTournamentDto, slot: TourSlotDto, set: TourHandicapSetDto) =
        call(token, "tour_start_slot", buildJsonObject {
            put("p_slot_id", slot.id); put("p_expected_revision", t.registrationRevision)
            put("p_set_id", set.id); put("p_set_revision", set.revision)
        })
}

internal fun effectiveTourSet(sets: List<TourHandicapSetDto>, tournament: TourTournamentDto): TourHandicapSetDto? =
    sets.filter { it.status == "confirmed" && tournament.startsOn != null && it.effectiveFrom <= tournament.startsOn }
        .maxByOrNull { it.effectiveFrom }

internal fun tourHandicapValue(group: String?, discipline: String): Int? {
    val index = "ABCDEF".indexOf(group?.singleOrNull() ?: return null)
    if (index < 0) return null
    return when (discipline) {
        "classic" -> listOf(23,14,7,4,1,0)[index]
        "nines" -> listOf(18,10,4,3,2,0)[index]
        "eights" -> listOf(14,8,4,3,2,0)[index]
        "narubak" -> listOf(-13,-9,-5,-3,-1,0)[index]
        else -> null
    }
}
